import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
} from "@angular/core";

/**
 * Interruptor de tema (spec 011 RF15). Dumb: recebe `dark` e emite `toggle`;
 * quem guarda o estado é o `ThemeService`.
 *
 * O botão inteiro é o controle — o trilho move o disco, e sol e lua trocam
 * de lugar em oposição, de modo que o ícone visível é sempre o do tema em
 * vigor, não o do destino. Movimento respeita `prefers-reduced-motion`.
 */
@Component({
  selector: "app-theme-toggle",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      role="switch"
      [attr.aria-checked]="dark()"
      [attr.aria-label]="
        dark() ? 'Mudar para o modo claro' : 'Mudar para o modo noturno'
      "
      (click)="toggle.emit()"
      class="theme-toggle"
      [class.is-dark]="dark()"
    >
      <span class="theme-toggle__track" aria-hidden="true">
        <span class="theme-toggle__icon theme-toggle__icon--sun">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
        </span>
        <span class="theme-toggle__icon theme-toggle__icon--moon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
          </svg>
        </span>
        <span class="theme-toggle__thumb"></span>
      </span>
      <span class="theme-toggle__label">{{
        dark() ? "Modo noturno" : "Modo claro"
      }}</span>
    </button>
  `,
  styles: [
    `
      .theme-toggle {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        width: 100%;
        min-height: 44px;
        padding: 0.75rem 1rem;
        border-radius: 0.75rem;
        /* Vive no aside: segue o chrome (spec 026), não o branco fixo. */
        color: var(--bf-chrome-text);
        transition: background-color 0.2s ease;
      }
      .theme-toggle:hover {
        background-color: var(--bf-hover);
      }
      .theme-toggle:focus-visible {
        outline: 2px solid var(--color-bf-gold-500, #dcac44);
        outline-offset: 2px;
      }

      .theme-toggle__track {
        position: relative;
        display: inline-flex;
        align-items: center;
        flex: none;
        width: 3.25rem;
        height: 1.75rem;
        padding: 0 0.25rem;
        border-radius: 999px;
        background: color-mix(in srgb, var(--bf-chrome-text) 14%, transparent);
        border: 1px solid var(--bf-chrome-border);
      }

      .theme-toggle__thumb {
        position: absolute;
        left: 0.1875rem;
        width: 1.375rem;
        height: 1.375rem;
        border-radius: 999px;
        background: linear-gradient(135deg, #eeddba, #dcac44);
        box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
        transition: transform 0.32s cubic-bezier(0.16, 1, 0.3, 1);
      }
      .is-dark .theme-toggle__thumb {
        transform: translateX(1.5rem);
      }

      .theme-toggle__icon {
        position: absolute;
        display: grid;
        place-items: center;
        width: 1rem;
        height: 1rem;
        color: color-mix(in srgb, var(--bf-chrome-text) 85%, transparent);
        transition: opacity 0.24s ease, transform 0.32s
          cubic-bezier(0.16, 1, 0.3, 1);
      }
      .theme-toggle__icon svg {
        width: 100%;
        height: 100%;
      }
      /* Sol à esquerda no claro; some e cede o lugar quando escurece. */
      .theme-toggle__icon--sun {
        left: 1.75rem;
        opacity: 1;
      }
      .theme-toggle__icon--moon {
        left: 0.5rem;
        opacity: 0;
        transform: scale(0.6);
      }
      .is-dark .theme-toggle__icon--sun {
        opacity: 0;
        transform: scale(0.6);
      }
      .is-dark .theme-toggle__icon--moon {
        opacity: 1;
        transform: none;
      }

      .theme-toggle__label {
        font-weight: 600;
        font-size: 0.95rem;
      }

      @media (prefers-reduced-motion: reduce) {
        .theme-toggle__thumb,
        .theme-toggle__icon {
          transition: none;
        }
      }
    `,
  ],
})
export class ThemeToggle {
  dark = input.required<boolean>();
  toggle = output<void>();
}
